import { createContext, type ReactNode, useContext, useRef } from 'react';
import { useGeolocated } from 'react-geolocated';
import { DEFAULT_MAP_CENTER } from '@/constants/mapConstants';
import { useLocationActions } from '@/stores/locationStore';
import { createLogger } from '@/utils/logger';

interface GeolocationContextType {
  refreshPosition: () => void;
  initPosition: () => void;
}

const logger = createLogger('useGeolocation');

const GeolocationContext = createContext<GeolocationContextType | null>(null);

interface GeolocationProviderProps {
  children: ReactNode;
}

export function GeolocationProvider({ children }: GeolocationProviderProps) {
  const { setLocation, setLocationLoading } = useLocationActions();
  const isInitialized = useRef(false);
  const isRefreshMode = useRef(false);

  // Use the react-geolocated hook
  const { getPosition } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
      timeout: 10_000, // 10 seconds
      maximumAge: 300_000, // 5 minutes
    },
    suppressLocationOnMount: true,
    onSuccess: (position) => {
      const { latitude, longitude, accuracy } = position.coords;

      // Update Zustand store
      setLocation({
        coordinates: { lat: latitude, lng: longitude },
        isActualUserLocation: true,
        accuracy,
        isLocationReady: true,
        changeReason: isRefreshMode.current ? 'move_to_current_location' : 'init',
      });
      setLocationLoading(false);

      logger.info('Geolocation success:', { latitude, longitude, accuracy });
    },
    onError: (error) => {
      let errorMessage = 'Unable to retrieve your location';

      if (error) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location services and refresh the page.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred';
            break;
        }
      }

      // Update Zustand store with fallback location
      setLocation({
        coordinates: DEFAULT_MAP_CENTER,
        isActualUserLocation: false,
        accuracy: null,
        isLocationReady: true,
        changeReason: 'fallback',
      });
      setLocationLoading(false);

      logger.warning('Geolocation failed, using default map center:', errorMessage);
    },
  });

  function getPositionWithLoading() {
    setLocationLoading(true);
    getPosition();
  }

  function initPosition() {
    if (!isInitialized.current) {
      getPositionWithLoading();
      isInitialized.current = true;
    }
  }

  function refreshPosition() {
    isRefreshMode.current = true;
    getPositionWithLoading();
  }

  return <GeolocationContext.Provider value={{ refreshPosition, initPosition }}>{children}</GeolocationContext.Provider>;
}

export function useGeolocation() {
  const context = useContext(GeolocationContext);
  if (!context) {
    throw new Error('useGeolocation must be used within a GeolocationProvider');
  }

  return context;
}
